/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useMemo } from 'react';
import { css } from 'aphrodite';
import { mixins } from '@blend/consumer-components';
import { CommonAPI } from '../../../api/common/responses';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { mediaBreakpoints, mediaOrientations } from '../../../common/responsive';
import { useVideoContext } from '../../../contexts/videoProvider/videoContext';
import { ConsumerAppConfigContext } from '../../../contexts/appConfig';
import { useConsumerTheme } from '../../../hooks/useConsumerTheme';
import { useDominantSpeaker } from '../../../hooks/videoProvider/useDominantSpeaker';
import { ParticipantIdentity } from './ParticipantIdentity';
import { ParticipantPin } from './ParticipantPin';
import { diagnostics } from '@utils/diagnostics';
import { find } from '@utils/lodash';

const VIDEO_TILE_HEIGHT = 156;
const VIDEO_MARGIN = 16;
const VIDEO_WRAPPER_ASPECT_RATIO_DEFAULT = 3 / 2;
const VIDEO_WRAPPER_ASPECT_RATIO_ZOOM = 16 / 9;
export namespace Participant {
  export interface Props {
    participant: VideoProvider.IParticipant;
    users: CommonAPI.UserDataResponse | null;
    pinned?: boolean;
    onClick?: (participant: VideoProvider.IParticipant) => void;
    isParticipantsTabView?: boolean;
  }
}

const desktopWidthDefault = (VIDEO_TILE_HEIGHT - VIDEO_MARGIN) * VIDEO_WRAPPER_ASPECT_RATIO_DEFAULT;
const desktopWidthZoom = (VIDEO_TILE_HEIGHT - VIDEO_MARGIN) * VIDEO_WRAPPER_ASPECT_RATIO_ZOOM;

const generateStyles = (appConfig: ConsumerAppConfigContext) => ({
  participantWrapper: {
    position: 'relative',
    display: 'flex',
    ':not(:last-child)': {
      ...mixins.margin(0, 4, 0, 0),
    },

    [mediaBreakpoints.desktop]: {
      height: `${VIDEO_TILE_HEIGHT - VIDEO_MARGIN}px`,
    },

    [mediaBreakpoints.mobile]: {
      [mediaOrientations.landscape]: {
        width: '100%',
        ':not(:last-child)': {
          ...mixins.margin(0, 0, 4, 0),
        },
      },
    },
  },
  wrapperOverridesForTwilio: {
    alignItems: 'center',
    [mediaBreakpoints.desktop]: {
      width: `${desktopWidthDefault}px`,
    },
    [mediaBreakpoints.mobile]: {
      [mediaOrientations.landscape]: {
        // setting minWidth to avoid video tile flashes when we are getting the next participant to show on mobile
        minHeight: '30%',
      },
      [mediaOrientations.portrait]: {
        height: '100%',
      },
    },
  },

  wrapperOverridesForZoom: {
    [mediaBreakpoints.desktop]: {
      width: `${desktopWidthZoom}px`,
    },
    [mediaBreakpoints.mobile]: {
      justifyContent: 'center',

      [mediaOrientations.portrait]: {
        aspectRatio: `${VIDEO_WRAPPER_ASPECT_RATIO_ZOOM}`,
        maxWidth: '32%',
      },

      [mediaOrientations.landscape]: {
        aspectRatio: `${VIDEO_WRAPPER_ASPECT_RATIO_ZOOM}`,
        overflow: 'hidden',
      },
    },

    ':after': {
      content: "''",
      background: appConfig.colors.darkerGray,
      width: '100%',
      height: '100%',
      position: 'absolute',
      display: 'block',
      zIndex: -1,
    },
  },
  participantWrapperMobileParticipantsView: {
    [mediaOrientations.portrait]: {
      maxWidth: '100%',
    },
  },
});

const Participant: React.FunctionComponent<Participant.Props> = ({
  participant,
  users,
  pinned,
  onClick,
  isParticipantsTabView,
}) => {
  const { styles } = useConsumerTheme(generateStyles);
  const { isMobileView } = useRootSelector(state => state.layout);
  const { videoProvider } = useVideoContext();
  const dominantSpeaker = useDominantSpeaker();
  const isDominantSpeaker = videoProvider.isDominantSpeaker(participant, dominantSpeaker);
  const userSession = find(users?.inVideoCall, { id: participant.identity });

  const identity = useMemo(() => {
    if (!!userSession) {
      const primaryUsers = userSession.users.filter(user => !user.primaryParticipantId);
      if (primaryUsers.length === 1) {
        return `${primaryUsers[0].firstName} ${primaryUsers[0].lastName}`;
      } else if (primaryUsers.length > 1) {
        return primaryUsers.map(user => user.lastName).join(', ');
      }
    }
    diagnostics.warn(`Failed to get user session identity`);
    return '';
  }, [userSession]);

  const ParticipantTrack = useMemo(() => {
    return videoProvider.getParticipantTrackComponent();
  }, [videoProvider]);

  const shouldOverrideAspectRatio = videoProvider.shouldOverrideAspectRatio();
  return (
    <div
      className={css(
        styles.participantWrapper,
        shouldOverrideAspectRatio ? styles.wrapperOverridesForZoom : styles.wrapperOverridesForTwilio,
        isParticipantsTabView && styles.participantWrapperMobileParticipantsView,
      )}
      key={participant.sid}
      onClick={() => onClick?.(participant)}
    >
      <ParticipantTrack data-it="ParticipantTracks" participant={participant} isDominantSpeaker={isDominantSpeaker} />
      {/*
        TODO: (ZOOMSDK) support ParticipantPin instead of always showing the participant name via `shouldOverrideAspectRatio`
      */}
      {isMobileView && !shouldOverrideAspectRatio
        ? pinned && <ParticipantPin isDominantSpeaker={isDominantSpeaker} data-it="ParticipantPin" />
        : identity && (
            <ParticipantIdentity
              data-it="ParticipantIdentity"
              identity={identity}
              isDominantSpeaker={isDominantSpeaker}
              participant={participant}
            />
          )}
    </div>
  );
};

export const MemoizedParticipant = React.memo(Participant);
